import axiosInstance from './axiosInstance';

// A separate scope avoids changing the offline reader's fetch/caching behavior.
export const PUSH_SCOPE = '/push/';
export const pushSupported = () => typeof window !== 'undefined' && window.isSecureContext
  && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export async function pushRegistration(create = false) {
  if (!pushSupported()) return null;
  let reg = await navigator.serviceWorker.getRegistration(PUSH_SCOPE);
  if (reg && new URL(reg.scope).pathname !== PUSH_SCOPE) reg = null;
  if (!reg && create) reg = await navigator.serviceWorker.register('/push-worker.js', { scope: PUSH_SCOPE });
  // The /push/ scope has no navigated page to trigger normal update checks.
  // Pick up click-handler fixes when settings are opened, without making
  // notification revocation or account switching depend on network access.
  if (reg) reg.update().catch(() => {});
  if (reg && !reg.active) {
    const worker = reg.installing || reg.waiting;
    if (!worker) throw new Error('Push worker unavailable');
    await new Promise((resolve, reject) => {
      const timer = setTimeout(() => { cleanup(); reject(new Error('Push worker timed out')); }, 15000);
      const cleanup = () => { clearTimeout(timer); worker.removeEventListener('statechange', changed); };
      const changed = () => {
        if (worker.state === 'activated') { cleanup(); resolve(); }
        else if (worker.state === 'redundant') { cleanup(); reject(new Error('Push worker installation failed')); }
      };
      worker.addEventListener('statechange', changed);
      changed();
    });
  }
  return reg;
}

export async function currentPush() {
  const reg = await pushRegistration();
  return reg ? reg.pushManager.getSubscription() : null;
}

function keyBytes(key) {
  const raw = atob(key.replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, c => c.charCodeAt(0));
}

async function pushStep(stage, operation) {
  try { return await operation(); }
  catch (error) {
    const failure = new Error(error.message || 'Unknown error');
    failure.name = error.name || 'Error';
    failure.pushStage = stage;
    failure.response = error.response;
    throw failure;
  }
}

export async function enablePush(publicKey) {
  // Called directly from a button, before any other asynchronous work.
  const permission = await pushStep('permission', () => Notification.requestPermission());
  if (permission !== 'granted') {
    const error = new Error('permission');
    error.pushStage = 'permission';
    throw error;
  }
  const reg = await pushStep('worker', () => pushRegistration(true));
  let sub = await pushStep('subscription', () => reg.pushManager.getSubscription());
  if (!sub) sub = await pushStep('subscription', () => reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) }));
  try {
    await pushStep('server', () => axiosInstance.post('/push/subscribe', sub.toJSON()));
  } catch (error) {
    // A failed binding must not leave an apparently enabled browser endpoint.
    await sub.unsubscribe();
    throw error;
  }
  return sub;
}

export async function disablePush() {
  const reg = await pushRegistration();
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    // Revoke at the browser even when the backend is offline or auth expired.
    const removed = await sub.unsubscribe();
    if (!removed && await reg.pushManager.getSubscription()) throw new Error('Could not disable notifications');
    await axiosInstance.post('/push/unsubscribe', { endpoint: sub.endpoint }, { timeout: 5000 }).catch(() => {});
  }
  const notifications = await reg.getNotifications();
  notifications.forEach(n => n.close());
}

export async function stopPushForAuthChange() {
  try { await disablePush(); }
  catch (error) {
    // Do not switch identity while a previous account's push remains live.
    window.alert('Could not disable notifications. Please retry. / Не удалось отключить уведомления. Повторите попытку.');
    throw error;
  }
}
